import type { Observable } from 'rxjs';

export interface TypeConverter<A, B> {
    convert(a: A): B;
}

export interface ObservableTypeConverter<A, B> {
    convert(a: A): Observable<B>;
}

interface SettledPromiseBase {
    status: 'fulfilled' | 'rejected';
}

interface FulfilledPromise<T> extends SettledPromiseBase {
    value: T;
}

interface RejectedPromise extends SettledPromiseBase {
    reason: Error | string;
}

type SettledPromise<T> = FulfilledPromise<T> | RejectedPromise;

/**
 * A polyfill for the Promise.allSettled API. Ensures that any given rejection of a promise within the array does
 * not cancel the entire array.
 * @param promises An array of Promises.
 * @returns An array of objects containing a status and either the expected unwrapped Promise value, or the reason for its rejection.
 */
export async function promiseAllSettled<T>(promises: Promise<T>[]): Promise<SettledPromise<T>[]> {
    return Promise.all(
        promises.map(async promise => {
            try {
                const value = await promise;
                return {
                    status: 'fulfilled',
                    value: value,
                } as FulfilledPromise<T>;
            } catch (error) {
                return {
                    status: 'rejected',
                    reason: error,
                } as RejectedPromise;
            }
        })
    );
}

/**
 * An extension to Promise.allSettled that returns only the values that succeeded from a given array of Promises.
 * Essentially ignores all errors and simply removes them from the resulting array.
 * @param promises An array of Promises that will be filtered on success
 * @returns An array of the expected type, for all promises that were fulfilled
 */
export async function promiseOnlyResolved<T>(promises: Promise<T>[]): Promise<T[]> {
    const settledPromises = await promiseAllSettled<T>(promises);
    return settledPromises
        .filter(promise => promise.status === 'fulfilled')
        .map(promise => (promise as FulfilledPromise<T>).value);
}


type EmptyCheckKey = 'all' | 'nullOrUndefined' | 'nullOnly' | 'undefinedOnly' | 'stringOnly';
const emptyChecker = new Map<EmptyCheckKey, ((object: any) => boolean)>([
    ['all',                 (object: any) => object === null || object === undefined || object === '' ],
    ['nullOrUndefined',     (object: any) => object === null || object === undefined ],
    ['nullOnly',            (object: any) => object === null ],
    ['undefinedOnly',       (object: any) => object === undefined ],
    ['stringOnly',          (object: any) => object === '' ],
]);
export interface DeepCompareConfig {
    debug?: boolean;
    emptyChecker?: EmptyCheckKey;
}

/**
 * Deep comparison of two objects, treats undefined and null as the same
 * @param config options object for adding debugging information or to modify "empty" checking logic
 * @returns true if the objects are equal, false if not
 */
export function deepCompare<T>(
    source: T,
    other: T,
    config?: DeepCompareConfig
): boolean {
    const configObj = {
        debug: config?.debug ?? false,
        emptyChecker: config?.emptyChecker ?? 'nullOrUndefined'
    };
    return deepCompareInternal(source, other, configObj, null);
}

function printFailure<T>(
    source: T,
    other: T,
    check: boolean,
    key: string,
    debugProp?: string
) {
    if (!check) {
        // eslint-disable-next-line max-len
        console.log(`Equals failure at prop: '${debugProp}'; failed check: '${key}'. Source value '${source}' is not equal to other value '${other}'`);
    }
};

/**
 * Overload for deepCompare, hides the unneeded debugProp from callers
 * @returns true if the objects are equal, false if not
 */
function deepCompareInternal<T>(
    source: T,
    other: T,
    config: DeepCompareConfig = { debug: false, emptyChecker: 'nullOrUndefined'},
    debugProp?: string
): boolean {
    const emptyCheckerFn = emptyChecker.get(config.emptyChecker);
    const sourceEmpty = emptyCheckerFn(source);
    const otherEmpty = emptyCheckerFn(other);
    const bothEmpty = sourceEmpty && otherEmpty;
    if (bothEmpty) {
        return true;
    }
    if (sourceEmpty !== otherEmpty) {
        if (config.debug) {
            printFailure(source, other, false, 'empty check', debugProp);
        }
        return false;
    }

    const bothArrays = Array.isArray(source) && Array.isArray(other);
    if (bothArrays) {
        const sourceAsArray = source as unknown as any[];
        const otherAsArray = other as unknown as any[];
        const check = (
            sourceAsArray.length === otherAsArray.length
            && sourceAsArray.every((sourceItem, i) => deepCompareInternal(sourceItem, other[i], config, `index ${i}`))
        );
        if (config.debug) {
            printFailure(source, other, check, 'array check', debugProp);
        }
        return check;
    }

    const bothObjects = (typeof source === 'object') && (typeof other === 'object');
    if (bothObjects) {
        let check = false;
        if (source instanceof Date && other instanceof Date) {
            check = source.valueOf() === other.valueOf();
        } else {
            check = Object.getOwnPropertyNames(source).every(prop => deepCompareInternal(source[prop], other[prop], config, prop));
        }
        if (config.debug) {
            printFailure(source, other, check, 'object check', debugProp);
        }
        return check;
    }

    const result = source === other;
    if (config.debug) {
        printFailure(source, other, result, 'primitive check', debugProp);
    }
    return result;
}

/**
 * Returns either the result of the division as expected, or 0 if the denominator isn't valid
 * TODO: check math-helper safeDivide and agree on consistent solution
 */
export function safeDivide(numerator: number, denominator: number): number {
    if (numerator === 0 || !denominator) {
        return 0;
    }

    return numerator / denominator;
}

/**
 * Trims string & appends ellipsis if > maxLength, otherwise return original string
 * @param str - string value that needs potential trimming
 * @param maxLength - number value to determine trim condition
 */
export function trimLongStringWithEllipsis(str: string, maxLength: number): string {
    return str.length > maxLength ? str.slice(0, maxLength - 3) + '...' : str;
}


export function getReadableFileSize(fileSize: number): string {
    let suffix = 'B';
    if (fileSize >= 1e9) {
        suffix = 'G' + suffix;
    } else if (fileSize >= 1e6) {
        suffix = 'M' + suffix;
    } else if (fileSize >= 1e3) {
        suffix = 'K' + suffix;
    }

    return fileSize.toString().substring(0, 1) + suffix;
}

export function base64Encode(str: string): string {
    return Buffer.from(str).toString('base64');
}

export function base64Decode(base64Str: string): string {
    return Buffer.from(base64Str, 'base64').toString();
}

/**
 * Converts a base64-encoded image string to a File object
 * @param base64String The base64-encoded image string (with or without data URL prefix)
 * @param fileName The name for the generated file
 * @param mimeType The MIME type of the image (optional, will try to detect from data URL)
 * @returns A File object created from the base64-encoded image
 */
export function base64ToFile(
    base64String: string,
    fileName: string,
    mimeType?: string
): File {
    // Check if the base64 string includes the data URL prefix
    let base64Data = base64String;

    // If it has a data URL prefix (e.g., "data:image/jpeg;base64,"), extract the MIME type and data
    if (base64String.includes(';base64,')) {
        // Extract MIME type if not provided
        if (!mimeType) {
            const matches = base64String.match(/^data:([A-Za-z-+/]+);base64,/);
            if (matches && matches.length > 1) {
                mimeType = matches[1];
            }
        }

        // Remove the data URL prefix
        base64Data = base64String.split(';base64,').pop() || '';
    }

    // Default to "image/png" if MIME type couldn't be detected
    if (!mimeType) {
        mimeType = 'image/png';
    }

    // Convert base64 to binary
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);

    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }

    // Create a Blob from the binary data
    const blob = new Blob([bytes], { type: mimeType });

    // Create and return a File object
    return new File([blob], fileName, {
        type: mimeType,
        lastModified: Date.now()
    });
}
